import React, { useCallback, useState } from 'react'
import type { ReactElement } from 'react'
import { useSession } from 'src/sdk/session'
import { useLocation } from '@reach/router'
import { Input } from '@faststore/ui'
import Button from 'src/components/ui/Button'
import axios from 'axios'
import { Bell as BellIcon } from 'phosphor-react'
import './styles.scss'

export interface OutOfStockProps {
  testId?: string
  buttonTxt?: string
  notificationMsg?: string
  title?: string
  icon?: ReactElement
  productName: string
  productId: string
  skuId: string
  image: {
    url: string
    alternateName: string
  }[] | undefined
}

type InsiderEventType = {
  customerName: string
  customerEmail: string
  productName: string
  productId: string
  sku: string
  date: string
  image: {
    url: string
    alternateName: string
  }[] | undefined
  href: string
}

function OutOfStock(props: OutOfStockProps) {
  const {
    title = 'Out of Stock',
    notificationMsg = 'Notify me when available',
    buttonTxt = 'Send',
    icon = <BellIcon />,
    testId = 'store-out-of-stock',
    productName,
    productId,
    skuId,
    image
  } = props

  const [email, setEmail] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [emailError, setEmailError] = useState<boolean>(false)
  const { person } = useSession()
  const { href } = useLocation()
  const hasEmptyField = !email || !name || !email?.trim() || !name?.trim()

  const handleInsiderEvent = useCallback((data: InsiderEventType) => {
    if (typeof window !== 'undefined') {
      if (person?.email) {
        window.insider_object = window.insider_object || {}

        window.insider_object.user = {
          ...window.insider_object.user,
          name: data.customerName,
          email: data.customerEmail,
          email_optin: true,
          custom: {
            ...window.insider_object.user?.custom,
            data_cadastro_avise_me: data.date,
            produto_avise_me: data.productId,
            sku_id_avise_me: data.sku,
            sku_name_avise_me: data.productName,
            sku_image_url_avise_me: image?.[0]?.url ?? null,
            sku_url_page_avise_me: data.href
          }
        }

        window.Insider.eventManager.dispatch("init-manager:re-initialize")
      } else {
        window.insider_object = window.insider_object || {}

        window.insider_object.user = {
          ...window.insider_object.user,
          name: data.customerName,
          email: data.customerEmail,
          email_optin: true,
          custom: {
            data_cadastro_avise_me: data.date,
            produto_avise_me: data.productId,
            sku_id_avise_me: data.sku,
            sku_name_avise_me: data.productName,
            sku_image_url_avise_me: image?.[0]?.url ?? null,
            sku_url_page_avise_me: data.href
          }
        }

        window.Insider.eventManager.dispatch("init-manager:re-initialize")
      }
    }
  }, [])

  const submitAvailabilyNotify =
    (customerEmail: string, customerName: string, sku: string) => async () => {
      const isValidEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(
        customerEmail
      )

      if (!isValidEmail) {
        setEmailError(true)

        return
      }

      try {
        setLoading(true)
        const date = new Date().toISOString()

        await axios.post('/api/productAvailability', {
          skuId: sku,
          name: customerName,
          email: customerEmail,
          notificationSend: 'false',
          createdAt: date,
        })

        handleInsiderEvent({
          customerName,
          customerEmail,
          productName,
          productId,
          sku,
          date,
          image,
          href
        })
        setSuccess(true)
      } catch (err) {
        console.error('Error while process notify me', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

  const handleChangeName = (event: React.ChangeEvent<{ value: string }>) => {
    setName(event?.target?.value)
  }

  const handleChangeEmail = (event: React.ChangeEvent<{ value: string }>) => {
    setEmailError(false)
    setEmail(event?.target?.value)
  }

  if (success) {
    return (
      <div data-fs-out-of-stock data-testid={testId} aria-live="polite">
        Pronto! Assim que o produto estiver disponível novamente nós avisaremos.
      </div>
    )
  }

  return (
    <div data-fs-out-of-stock data-testid={testId} aria-live="polite">
      <p>{title}</p>
      <p className="outOfStock__message">
        {icon} {notificationMsg}
      </p>
      <div className="outOfStock__inputsContainer">
        <label className="outOfStock__label" htmlFor="name">
          Nome
        </label>
        <Input
          id="name"
          className="outOfStock__inputName"
          aria-label="Name"
          value={name}
          onChange={handleChangeName}
          placeholder="Nome"
          disabled={loading}
        />

        <label className="outOfStock__label" htmlFor="email">
          E-mail
        </label>
        <Input
          id="email"
          className="outOfStock__inputEmail"
          type="email"
          aria-label="Email"
          value={email}
          onChange={handleChangeEmail}
          placeholder="E-mail"
          disabled={loading}
        />
        {emailError && (
          <span className="outOfStock__error">E-mail inválido</span>
        )}
      </div>
      <Button
        className="outOfStock__submitButton"
        variant="primary"
        onClick={submitAvailabilyNotify(email, name, skuId)}
        disabled={loading || hasEmptyField}
      >
        {loading ? 'Cadastrando' : buttonTxt}
      </Button>
      {error && (
        <span className="outOfStock__error">
          Falha ao se cadastrar, tente novamente
        </span>
      )}
    </div>
  )
}

export default OutOfStock
